import hero640 from '@/assets/hero-landscape-640.avif';
import hero1280 from '@/assets/hero-landscape-1280.avif';
import hero1920 from '@/assets/hero-landscape-1920.avif';
import hero2560 from '@/assets/hero-landscape-2560.avif';

// Imported (not referenced from public/) so Vite emits them under assets/ with a
// content hash — that is what makes the immutable 1-year Cache-Control in
// .github/workflows/deploy.yml safe to apply.
//
// The source plate is 6144×4096. Shipping it whole cost 181KB on the wire but,
// far worse, made every phone decode 25 megapixels (~100MB of RGBA) on the main
// thread before first paint. The ladder below tops out at 2560px, which is
// ample for a full-bleed decorative backdrop.
//
// The AVIFs must not carry a `clli` (Content Light Level Info) box: Firefox's
// AVIF decoder fails to parse it and silently renders nothing
// (https://bugzilla.mozilla.org/show_bug.cgi?id=1805436), even though the
// image is plain SDR (unspecified transfer characteristics) and never needed
// that HDR metadata. Re-encode with `avifenc` (no `--clli` flag) if these are
// regenerated — don't run them back through a pipeline that adds it.
const HERO_SRCSET = [
  `${hero640} 640w`,
  `${hero1280} 1280w`,
  `${hero1920} 1920w`,
  `${hero2560} 2560w`,
].join(', ');

/**
 * The hero-34 landscape, pinned to the top of the page for exactly one viewport
 * so it sits behind the masthead as well as the hero itself. Rendered as a
 * sibling of <LandingHeader> rather than inside <Hero>, because the header is
 * in-flow above the hero and can't be reached from within it.
 *
 * The scrim is what makes the photo theme-safe: it resolves to the page
 * background at the bottom in both themes, so the band fades into the trust
 * strip instead of cutting off. Light mode barely veils the photo — the ink
 * reads straight off the pale landscape. Dark mode needs the heavy scrim,
 * because near-white text over a light photograph is unreadable without one.
 */
export function HeroBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-svh">
      <img
        src={hero1280}
        srcSet={HERO_SRCSET}
        /* Full-bleed: the image always spans the viewport width, so the
           candidate picked should track viewport width 1:1. */
        sizes="100vw"
        alt=""
        className="size-full object-cover object-center"
        fetchPriority="high"
        /* async, not sync: this is a decorative backdrop behind the copy, so a
           blocking decode buys nothing and stalls the main thread on phones. */
        decoding="async"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/10 via-background/5 to-background dark:from-background/85 dark:via-background/75 dark:to-background" />
    </div>
  );
}
